/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

/** Current application (customization) version. Bump this when you add to the changelog. */
export const APP_VERSION = '2.18.0';

export type ChangeType = 'feature' | 'improvement' | 'fix';

export interface ChangeItem {
  type: ChangeType;
  text: string;
  /** The same change in Thai — what most people here read. */
  th: string;
}

export interface ChangelogEntry {
  version: string;
  /** ISO date, shown in the reader's own calendar. */
  date: string;
  title?: string;
  titleTh?: string;
  changes: ChangeItem[];
}

/** Newest first. Every change carries its Thai text too. */
export const CHANGELOG: ChangelogEntry[] = [
  {
    version: '2.18.0',
    date: '2026-10-26',
    title: 'Act on several rows at once',
    titleTh: 'เลือกหลายรายการแล้วสั่งพร้อมกัน',
    changes: [
      {
        type: 'feature',
        text: 'All documents: tick rows to download them (several as a zip), add them to favourites or move them to the trash',
        th: 'เอกสารทั้งหมด: เลือกหลายรายการเพื่อดาวน์โหลด (หลายไฟล์ได้เป็น zip) เพิ่มในรายการโปรด หรือย้ายไปถังขยะ'
      },
      {
        type: 'feature',
        text: 'Users: tick users to enable or suspend them, add them to a group or export them; your own account is never suspended',
        th: 'ผู้ใช้งาน: เลือกหลายคนเพื่อเปิดใช้งาน ระงับ เพิ่มเข้ากลุ่ม หรือส่งออก โดยไม่ระงับบัญชีของคุณเอง'
      },
      {
        type: 'improvement',
        text: 'Each action says how many rows it did, and which failed and why',
        th: 'แจ้งผลว่าทำสำเร็จกี่รายการ และรายการใดไม่สำเร็จเพราะอะไร'
      }
    ]
  },
  {
    version: '2.17.0',
    date: '2026-10-25',
    title: 'Forms on your own paper',
    titleTh: 'พิมพ์แบบฟอร์มลงกระดาษของหน่วยงาน',
    changes: [
      {
        type: 'feature',
        text: 'A form can print onto a paper form the organisation already uses: choose its PDF or a scan, then place each field on its blank',
        th: 'แบบฟอร์มพิมพ์ลงบนกระดาษแบบฟอร์มที่หน่วยงานใช้อยู่ได้ เลือก PDF หรือภาพสแกน แล้ววางแต่ละช่องลงบนช่องว่างของกระดาษ'
      },
      {
        type: 'improvement',
        text: 'Amounts in words, the parts of a date, ticks, one character per box and table rows fill the paper; a table takes only as many rows as the paper has lines for',
        th: 'จำนวนเงินเป็นตัวอักษร วัน เดือน ปี เครื่องหมายถูก ตัวอักษรช่องละตัว และแถวตาราง ลงตามที่บนกระดาษ ตารางรับแถวได้เท่าที่กระดาษมีบรรทัด'
      }
    ]
  },
  {
    version: '2.16.0',
    date: '2026-10-25',
    title: 'Draft a form with AI',
    titleTh: 'ร่างแบบฟอร์มด้วย AI',
    changes: [
      {
        type: 'feature',
        text: 'Administrators can turn a form already in use (a photo, a PDF or a Word file) into a draft in the form builder with AI',
        th: 'ผู้ดูแลระบบให้ AI ร่างแบบฟอร์มจากแบบฟอร์มที่ใช้อยู่ (รูปถ่าย PDF หรือไฟล์ Word) เป็นร่างในตัวสร้างแบบฟอร์มได้'
      },
      {
        type: 'improvement',
        text: 'The draft opens unsaved for checking; it is off by default and follows the confidentiality rules',
        th: 'ร่างเปิดให้ตรวจก่อนบันทึก ปิดไว้เป็นค่าเริ่มต้น และใช้กฎชั้นความลับเหมือนเอกสารอื่น'
      }
    ]
  },
  {
    version: '2.15.0',
    date: '2026-10-25',
    title: 'Thai address field',
    titleTh: 'ช่องที่อยู่ในแบบฟอร์ม',
    changes: [
      {
        type: 'feature',
        text: 'Forms can ask for a Thai address: find the area by typing any part of it, or pick province, district and subdistrict; the postcode fills itself in',
        th: 'ช่องที่อยู่ในแบบฟอร์ม ค้นหาด้วยตำบล อำเภอ จังหวัด หรือรหัสไปรษณีย์ หรือเลือกจังหวัด อำเภอ ตำบล แล้วรหัสไปรษณีย์จะเติมให้เอง'
      },
      {
        type: 'improvement',
        text: 'The address prints on one line as a letter writes it, is checked against the official list, and the report exports its province and postcode as columns',
        th: 'ที่อยู่พิมพ์ใน PDF เป็นบรรทัดเดียว ตรวจกับรายชื่อพื้นที่ทางการ และรายงานส่งออกจังหวัดกับรหัสไปรษณีย์เป็นคอลัมน์แยก'
      }
    ]
  },
  {
    version: '2.14.0',
    date: '2026-10-24',
    title: 'Form submissions report',
    titleTh: 'รายงานคำตอบแบบฟอร์ม',
    changes: [
      {
        type: 'feature',
        text: 'Administrators can see every filed submission of a form, with each answer in its own column, search them and narrow them by date',
        th: 'ผู้ดูแลระบบดูคำตอบทุกฉบับของแต่ละแบบฟอร์มได้ แยกคำตอบแต่ละช่องเป็นคอลัมน์ ค้นหาและกรองตามช่วงวันที่ส่งได้'
      },
      {
        type: 'feature',
        text: 'Submissions can be exported as CSV or as an Excel workbook, with amounts as numbers and each table on its own sheet',
        th: 'ส่งออกคำตอบเป็น CSV หรือ Excel ได้ ตัวเลขและจำนวนเงินเป็นตัวเลขที่รวมได้ และตารางแยกชีตของตัวเอง'
      }
    ]
  },
  {
    version: '2.13.0',
    date: '2026-10-24',
    title: 'Signatures on forms',
    titleTh: 'เซ็นชื่อบนแบบฟอร์ม',
    changes: [
      {
        type: 'feature',
        text: 'A form can ask for a signature, drawn with a mouse, finger or pen, or typed as a name; the PDF prints it in its section or on the sender’s line at the foot',
        th: 'แบบฟอร์มมีช่องลายเซ็นได้ เซ็นด้วยเมาส์ นิ้ว หรือปากกา หรือพิมพ์ชื่อแทน แล้ว PDF จะพิมพ์ลายเซ็นในส่วนนั้น หรือบนเส้นลงชื่อของผู้ส่งท้ายแบบฟอร์ม'
      }
    ]
  },
  {
    version: '2.12.0',
    date: '2026-10-24',
    title: 'Forms that go for approval',
    titleTh: 'แบบฟอร์มที่ส่งเข้าอนุมัติได้',
    changes: [
      {
        type: 'feature',
        text: 'A submitted form can start an approval workflow, with the PDF as its document; its steps can route on the answers, e.g. orders over 100,000 go to a director first',
        th: 'ส่งแบบฟอร์มแล้วเริ่มเวิร์กโฟลว์อนุมัติได้ทันที โดยใช้ PDF เป็นเอกสาร และแต่ละขั้นเลือกเส้นทางตามคำตอบได้ เช่น ยอดเกิน 100,000 ให้ผู้บริหารอนุมัติก่อน'
      },
      {
        type: 'feature',
        text: 'A form can sign every filed PDF with the organisation’s digital certificate, naming the person who sent it',
        th: 'ตั้งให้ PDF ที่ยื่นทุกฉบับลงนามดิจิทัลด้วยใบรับรองของหน่วยงาน โดยระบุชื่อผู้ส่งได้'
      },
      {
        type: 'fix',
        text: 'Form PDFs no longer show stray Thai tone marks at the bottom of a page',
        th: 'PDF ของแบบฟอร์มไม่มีวรรณยุกต์หรือสระลอยอยู่ท้ายหน้าอีก'
      }
    ]
  },
  {
    version: '2.11.0',
    date: '2026-10-24',
    title: 'Smarter forms',
    titleTh: 'แบบฟอร์มที่ฉลาดขึ้น',
    changes: [
      {
        type: 'feature',
        text: 'Forms: show a field or a whole section only when another answer calls for it; hidden fields are not asked and not printed',
        th: 'แบบฟอร์ม: ตั้งให้ช่องหรือทั้งส่วนแสดงเฉพาะเมื่อคำตอบอื่นเข้าเงื่อนไข ช่องที่ซ่อนจะไม่ถูกถามและไม่พิมพ์ลง PDF'
      },
      {
        type: 'feature',
        text: 'Calculated fields and table columns — quantity × price, totals, VAT — worked out as people type and again when the PDF is printed',
        th: 'ช่องและคอลัมน์คำนวณ เช่น จำนวน × ราคา ยอดรวม ภาษีมูลค่าเพิ่ม คำนวณให้ขณะกรอก และคำนวณซ้ำตอนสร้าง PDF'
      },
      {
        type: 'feature',
        text: 'A form’s paper can carry the organisation’s logo and accent colour, turn to landscape, or use a compact layout',
        th: 'ใส่โลโก้และสีหลักของหน่วยงานบนเอกสาร เลือกกระดาษแนวนอน หรือจัดหน้าแบบกระชับได้'
      }
    ]
  },
  {
    version: '2.10.0',
    date: '2026-10-24',
    title: 'Forms that become PDFs',
    titleTh: 'แบบฟอร์มที่กรอกแล้วได้ PDF',
    changes: [
      {
        type: 'feature',
        text: 'Forms: fill in a published form, and get a PDF laid out like the paper one, with a reference number and signature boxes, filed in the system',
        th: 'แบบฟอร์ม: กรอกแบบฟอร์มที่เผยแพร่แล้ว ได้ PDF ตามรูปแบบของฟอร์ม พร้อมเลขที่อ้างอิงและช่องลงชื่อ และจัดเก็บเข้าระบบให้ทันที'
      },
      {
        type: 'feature',
        text: 'Administrators build forms by dragging in fields — amounts in baht, Buddhist Era dates, choices, Thai ID numbers, tables — and preview them first',
        th: 'ผู้ดูแลลากช่องมาวางสร้างแบบฟอร์มได้เอง ทั้งจำนวนเงิน วันที่ พ.ศ. ตัวเลือก เลขบัตรประชาชน และตาราง แล้วดูตัวอย่างก่อนเผยแพร่'
      }
    ]
  },
  {
    version: '2.9.1',
    date: '2026-10-24',
    changes: [
      {
        type: 'fix',
        text: 'The organisation overview, share links and PDPA report scroll again: what sat below the window could not be reached',
        th: 'หน้าภาพรวมองค์กร ลิงก์แชร์ และรายงาน PDPA เลื่อนดูได้แล้ว เดิมข้อมูลที่อยู่ใต้ขอบจอเลื่อนลงไปดูไม่ได้'
      },
      {
        type: 'fix',
        text: 'On a narrow window the document page shows the whole PDF studio and scrolls down through all of the details',
        th: 'หน้าเอกสารบนจอแคบแสดง PDF Studio ครบ และเลื่อนดูรายละเอียดได้จนสุด'
      }
    ]
  },
  {
    version: '2.9.0',
    date: '2026-10-22',
    title: 'Dates in the Buddhist Era everywhere',
    titleTh: 'วันที่เป็น พ.ศ. ทุกช่อง',
    changes: [
      {
        type: 'improvement',
        text: 'Date fields take Buddhist Era years, such as 20/05/2567, and open a Thai calendar; Common Era years still work',
        th: 'ช่องกรอกวันที่รับปี พ.ศ. เช่น 20/05/2567 และมีปฏิทินภาษาไทยให้เลือก ถ้าพิมพ์ปี ค.ศ. ก็ยังใช้ได้'
      },
      {
        type: 'improvement',
        text: 'Dates in the details panel of a document now read in the Buddhist Era like the rest of the system',
        th: 'วันที่ในแผงรายละเอียดเอกสารแสดงเป็น พ.ศ. เหมือนส่วนอื่นของระบบแล้ว'
      }
    ]
  },
  {
    version: '2.8.1',
    date: '2026-10-22',
    changes: [
      {
        type: 'fix',
        text: 'My Work and the organisation overview keep showing what they can when the finished workflows cannot be read, and say which part is missing',
        th: 'หน้างานของฉันและภาพรวมองค์กรยังแสดงส่วนที่อ่านได้ เมื่ออ่านประวัติเวิร์กโฟลว์ที่จบแล้วไม่ได้ และบอกว่าส่วนไหนขาดไป'
      }
    ]
  },
  {
    version: '2.8.0',
    date: '2026-10-22',
    title: 'Workflow Builder as a diagram',
    titleTh: 'ตัวสร้างเวิร์กโฟลว์แบบแผนภาพ',
    changes: [
      {
        type: 'feature',
        text: 'Draw workflows as a diagram: approvals with separate approved and rejected paths, conditions on document details, and work done in parallel',
        th: 'วาดเวิร์กโฟลว์เป็นแผนภาพได้ ทั้งขั้นอนุมัติที่แยกทางอนุมัติ/ไม่อนุมัติ เงื่อนไขตามข้อมูลเอกสาร และงานที่ทำพร้อมกันหลายคน'
      },
      {
        type: 'feature',
        text: "Publishing makes the diagram a real process in Alfresco's workflow engine, which runs every step itself",
        th: 'กดเผยแพร่แล้วแผนภาพจะกลายเป็นกระบวนการจริงในเครื่องมือเวิร์กโฟลว์ของ Alfresco ซึ่งเดินทุกขั้นเอง'
      },
      {
        type: 'improvement',
        text: 'A flow is a draft until published, and flows already running keep the version they started with',
        th: 'เวิร์กโฟลว์เป็นร่างจนกว่าจะเผยแพร่ และงานที่เริ่มไปแล้วยังใช้รุ่นเดิมจนจบ'
      }
    ]
  },
  {
    version: '2.7.0',
    date: '2026-10-22',
    title: 'Real digital signatures',
    titleTh: 'ลายเซ็นดิจิทัลจริง',
    changes: [
      {
        type: 'feature',
        text: "Digital signature in PDF Studio now signs the PDF cryptographically with the organisation's certificate, in your name",
        th: 'ปุ่มลายเซ็นดิจิทัลใน PDF Studio ลงนามเอกสารด้วยใบรับรองขององค์กรจริงในชื่อของคุณ ตรวจสอบได้ใน Adobe Reader'
      },
      {
        type: 'feature',
        text: 'PDF Studio shows each signature and whether the document changed after it was signed',
        th: 'PDF Studio แสดงลายเซ็นทุกรายการในเอกสาร และบอกว่าเอกสารถูกแก้ไขหลังลงนามหรือไม่'
      },
      {
        type: 'improvement',
        text: 'A second person can sign without breaking the first signature; saving edits over a signed PDF asks first',
        th: 'ลงนามต่อกันหลายคนได้โดยลายเซ็นเดิมยังถูกต้อง และถ้าจะบันทึกการแก้ไขทับเอกสารที่ลงนามแล้ว ระบบจะถามก่อน'
      }
    ]
  },
  {
    version: '2.6.0',
    date: '2026-10-22',
    title: 'Automatic expiry',
    titleTh: 'หมดอายุอัตโนมัติ',
    changes: [
      {
        type: 'feature',
        text: 'Automatic expiry can be turned on from the expiry page: every night, documents past their date move to Archive and are marked expired',
        th: 'เปิดหมดอายุอัตโนมัติได้จากหน้าวันหมดอายุ ทุกคืนเอกสารที่เลยวันหมดอายุจะย้ายไป Archive และถูกตั้งสถานะเป็นหมดอายุ'
      },
      {
        type: 'improvement',
        text: 'An archived document keeps exactly the readers it had, and the page shows the next run and what the last one moved',
        th: 'เอกสารที่ย้ายไป Archive ยังเปิดได้โดยคนเดิมทุกคน และหน้าวันหมดอายุแสดงเวลาทำงานครั้งถัดไปกับผลครั้งล่าสุด'
      }
    ]
  },
  {
    version: '2.5.1',
    date: '2026-10-22',
    changes: [
      {
        type: 'fix',
        text: 'All documents lists the departments to filter by again, each with its number of documents',
        th: 'หน้าเอกสารทั้งหมดมีรายการหน่วยงานให้เลือกกรองแล้ว พร้อมจำนวนเอกสารของแต่ละหน่วยงาน'
      }
    ]
  },
  {
    version: '2.5.0',
    date: '2026-10-22',
    title: 'A tour for new users',
    titleTh: 'ทัวร์แนะนำสำหรับผู้ใช้ใหม่',
    changes: [
      {
        type: 'feature',
        text: 'A short tour on first sign-in shows uploading with OCR and AI, search, Ctrl/⌘ K, work and the assistant',
        th: 'ทัวร์สั้น ๆ ตอนเข้าใช้ครั้งแรก แนะนำการอัปโหลดพร้อม OCR และ AI การค้นหา Ctrl/⌘ K งานของฉัน และผู้ช่วย AI'
      },
      {
        type: 'feature',
        text: 'The ? button opens a help menu: the tour again, the command palette, what is new, and the Alfresco manual',
        th: 'ปุ่ม ? เปิดเมนูช่วยเหลือ: เริ่มทัวร์อีกครั้ง เมนูลัด สิ่งที่ใหม่ และคู่มือ Alfresco'
      },
      {
        type: 'improvement',
        text: 'What is new is in Thai, and a dot marks it after each update until it is read',
        th: 'หน้าสิ่งที่ใหม่เป็นภาษาไทย และมีจุดเตือนหลังอัปเดตจนกว่าจะเปิดอ่าน'
      }
    ]
  },
  {
    version: '2.4.0',
    date: '2026-10-22',
    title: 'Organisation overview',
    titleTh: 'ภาพรวมองค์กร',
    changes: [
      {
        type: 'feature',
        text: 'Organisation overview shows documents by status, department, type and level, uploads per month, and how long approvals take',
        th: 'หน้าภาพรวมองค์กร แสดงเอกสารตามสถานะ หน่วยงาน ประเภท และชั้นความลับ เอกสารใหม่รายเดือน และเวลาที่ใช้อนุมัติ'
      },
      {
        type: 'improvement',
        text: 'All documents opens with a department already filtered when linked from the overview',
        th: 'กดหน่วยงานจากหน้าภาพรวมแล้ว หน้าเอกสารทั้งหมดเปิดพร้อมกรองหน่วยงานนั้นไว้'
      }
    ]
  },
  {
    version: '2.3.0',
    date: '2026-10-21',
    title: 'Personal data report',
    titleTh: 'รายงานข้อมูลส่วนบุคคล',
    changes: [
      {
        type: 'feature',
        text: 'Personal data report (PDPA) in Settings → Administration finds Thai IDs, card and account numbers, phones and emails, and shows them masked',
        th: 'การตั้งค่า → ผู้ดูแลระบบ → รายงาน PDPA หาเลขบัตรประชาชน เลขบัตร เลขบัญชี เบอร์โทร และอีเมลทั้งคลัง แสดงเฉพาะค่าที่ปกปิดแล้ว'
      }
    ]
  },
  {
    version: '2.2.0',
    date: '2026-10-21',
    title: 'Upload suggestions and share links',
    titleTh: 'AI ช่วยกรอกตอนอัปโหลด และหน้าลิงก์แชร์',
    changes: [
      {
        type: 'feature',
        text: 'Suggest with AI in the upload form fills empty details from the file before it is filed',
        th: 'ปุ่ม "ให้ AI แนะนำ" ในหน้าอัปโหลด เติมข้อมูลเอกสารที่ยังว่างจากเนื้อหาในไฟล์'
      },
      {
        type: 'feature',
        text: 'Share links lists every public link with its expiry and how often it was opened, and revokes one in a click',
        th: 'หน้าลิงก์แชร์ แสดงลิงก์สาธารณะทั้งหมด วันหมดอายุ และจำนวนครั้งที่เปิด ยกเลิกลิงก์ได้ในคลิกเดียว'
      }
    ]
  },
  {
    version: '2.1.0',
    date: '2026-10-21',
    title: 'Fixes from the menu audit',
    titleTh: 'แก้ไขจากการตรวจเมนู',
    changes: [
      {
        type: 'feature',
        text: 'Home is back in the sidebar, and Ctrl/⌘ K opens a command palette for menus and documents',
        th: 'หน้าหลักกลับมาอยู่ในแถบเมนู และ Ctrl/⌘ K เปิดเมนูลัดสำหรับไปยังเมนูและเอกสาร'
      },
      {
        type: 'improvement',
        text: 'Items per page and the landing page chosen in Settings now take effect',
        th: 'จำนวนรายการต่อหน้าและหน้าแรกที่ตั้งไว้ในการตั้งค่ามีผลจริงแล้ว'
      },
      { type: 'fix', text: 'The attach-file button on a task works again', th: 'ปุ่มแนบไฟล์ในงานกลับมาใช้ได้' },
      {
        type: 'fix',
        text: 'Search and filters on the workflow catalogue, and the status filter when starting a workflow, respond',
        th: 'ช่องค้นหาและตัวกรองในหน้าเวิร์กโฟลว์ และตัวกรองสถานะตอนเริ่มเวิร์กโฟลว์ ทำงานแล้ว'
      },
      {
        type: 'fix',
        text: 'A menu hidden by a policy also closes the addresses under it, such as workflows/start',
        th: 'เมนูที่ถูกซ่อนตามสิทธิ์ ปิดที่อยู่ย่อยของเมนูนั้นด้วย เช่น workflows/start'
      },
      {
        type: 'fix',
        text: 'Starting a template whose first step has nobody assigned no longer leaves the uploaded files behind',
        th: 'เริ่มเทมเพลตที่ขั้นแรกยังไม่มีผู้รับงาน จะไม่ทิ้งไฟล์ที่อัปโหลดค้างไว้อีก'
      },
      {
        type: 'fix',
        text: 'Saved searches, the user CSV export and Workflow Builder messages show words instead of translation keys',
        th: 'การค้นหาที่บันทึกไว้ ไฟล์ CSV ผู้ใช้ และข้อความใน Workflow Builder แสดงเป็นคำแทนรหัสข้อความ'
      },
      {
        type: 'fix',
        text: 'One notification bell, whose badge clears when you close it',
        th: 'เหลือกระดิ่งแจ้งเตือนอันเดียว และตัวเลขหายเมื่อปิดรายการ'
      }
    ]
  },
  {
    version: '2.0.0',
    date: '2026-10-07',
    title: 'Enterprise DMS',
    titleTh: 'ระบบจัดการเอกสารองค์กร',
    changes: [
      {
        type: 'feature',
        text: 'Thai-first interface in three languages, with Buddhist Era dates',
        th: 'หน้าจอภาษาไทยเป็นหลัก รองรับสามภาษา และแสดงวันที่เป็นปี พ.ศ.'
      },
      {
        type: 'feature',
        text: 'All documents, advanced search, favourites, trash and a copy/move workspace',
        th: 'เอกสารทั้งหมด ค้นหาขั้นสูง รายการโปรด ถังขยะ และหน้าคัดลอก/ย้ายเอกสาร'
      },
      {
        type: 'feature',
        text: 'Upload with document metadata, tags, and OCR for scans and images',
        th: 'อัปโหลดพร้อมข้อมูลเอกสาร แท็ก และ OCR สำหรับไฟล์สแกนและรูปภาพ'
      },
      {
        type: 'feature',
        text: 'Workflow: start wizard, My Work inbox, tracking, template catalogue and builder',
        th: 'เวิร์กโฟลว์: ขั้นตอนเริ่มงาน งานของฉัน การติดตาม แคตตาล็อกเทมเพลต และตัวสร้างเวิร์กโฟลว์'
      },
      {
        type: 'feature',
        text: 'PDF studio with personal-data detection and burned-in redaction',
        th: 'PDF Studio ตรวจหาข้อมูลส่วนบุคคล และปกปิดแบบถาวร'
      },
      {
        type: 'feature',
        text: 'AI assistant: ask about your documents, summarise, compare versions, suggest details',
        th: 'ผู้ช่วย AI: ถามเกี่ยวกับเอกสาร สรุป เปรียบเทียบเวอร์ชัน และแนะนำข้อมูลเอกสาร'
      },
      {
        type: 'feature',
        text: 'Administration: users, groups, audit log, email templates, watermark, menu permissions, scheduled jobs and data migration',
        th: 'งานผู้ดูแลระบบ: ผู้ใช้ กลุ่ม บันทึกการตรวจสอบ เทมเพลตอีเมล ลายน้ำ สิทธิ์เมนู งานตั้งเวลา และการย้ายข้อมูล'
      },
      {
        type: 'feature',
        text: 'Document expiry with a dashboard widget, and an installable app',
        th: 'วันหมดอายุเอกสารพร้อมการ์ดในหน้าหลัก และติดตั้งเป็นแอปบนเครื่องได้'
      }
    ]
  },
  {
    version: '1.3.0',
    date: '2026-05-31',
    title: 'Document view & sign-in',
    titleTh: 'หน้าเอกสารและหน้าเข้าสู่ระบบ',
    changes: [
      {
        type: 'feature',
        text: 'Opening a file now shows an in-layout document view — preview and details side by side, no full-screen takeover',
        th: 'เปิดไฟล์แล้วแสดงตัวอย่างและรายละเอียดคู่กันภายในหน้า ไม่เต็มจอ'
      },
      {
        type: 'feature',
        text: 'Document tabs: View, Properties (editable metadata, 2 columns), Permissions and History',
        th: 'แท็บเอกสาร: ดูเอกสาร คุณสมบัติ (แก้ข้อมูลได้ สองคอลัมน์) สิทธิ์ และประวัติ'
      },
      {
        type: 'feature',
        text: 'Share dialog with a public link, plus the full “more” actions menu (move, copy, delete, edit offline, aspects…)',
        th: 'หน้าต่างแชร์ลิงก์สาธารณะ และเมนูคำสั่งเพิ่มเติมครบ (ย้าย คัดลอก ลบ แก้ไขออฟไลน์ aspect …)'
      },
      {
        type: 'feature',
        text: 'Version badge with Manage Versions, add/remove tags inline, and comments on the file',
        th: 'ป้ายเวอร์ชันพร้อมจัดการเวอร์ชัน เพิ่ม/ลบแท็กในหน้า และความคิดเห็นบนไฟล์'
      },
      {
        type: 'feature',
        text: 'Redesigned sign-in page — two-panel layout with social buttons and brand artwork',
        th: 'ออกแบบหน้าเข้าสู่ระบบใหม่แบบสองส่วน'
      },
      {
        type: 'improvement',
        text: 'Version badge and preview refresh automatically after a new version is uploaded',
        th: 'ป้ายเวอร์ชันและตัวอย่างไฟล์รีเฟรชเองหลังอัปโหลดเวอร์ชันใหม่'
      },
      {
        type: 'fix',
        text: 'Manage Versions no longer errors on files without a version history',
        th: 'จัดการเวอร์ชันไม่ error กับไฟล์ที่ไม่มีประวัติเวอร์ชันอีก'
      },
      {
        type: 'fix',
        text: 'Sign-in validation messages no longer overlap the input fields',
        th: 'ข้อความแจ้งเตือนในหน้าเข้าสู่ระบบไม่ทับช่องกรอกอีก'
      }
    ]
  },
  {
    version: '1.2.0',
    date: '2026-05-30',
    title: 'Sidebar & account refresh',
    titleTh: 'ปรับแถบเมนูและบัญชีผู้ใช้',
    changes: [
      {
        type: 'feature',
        text: 'User profile, notifications and theme switch are now in the sidebar footer',
        th: 'โปรไฟล์ การแจ้งเตือน และการสลับธีม ย้ายไปอยู่ท้ายแถบเมนู'
      },
      { type: 'feature', text: 'App version and this changelog added to the sidebar', th: 'เพิ่มเวอร์ชันแอปและหน้าสิ่งที่ใหม่ในแถบเมนู' },
      {
        type: 'improvement',
        text: 'Whole UI scaled to 80% so more content fits on screen',
        th: 'ย่อหน้าจอทั้งหมดเหลือ 80% เพื่อให้เห็นข้อมูลมากขึ้น'
      },
      {
        type: 'fix',
        text: 'Dark mode: empty lists, table icons and page headers now render clearly',
        th: 'โหมดมืด: รายการว่าง ไอคอนในตาราง และหัวหน้าแสดงชัดขึ้น'
      }
    ]
  },
  {
    version: '1.1.0',
    date: '2026-05-29',
    title: 'Files & workflow polish',
    titleTh: 'ปรับหน้าไฟล์และเวิร์กโฟลว์',
    changes: [
      { type: 'feature', text: 'Gradient hero header + card layout on every file page', th: 'หัวหน้าแบบไล่สีและการ์ดในทุกหน้าไฟล์' },
      {
        type: 'feature',
        text: '“Start workflow” action from the file toolbar and right-click menu',
        th: 'เริ่มเวิร์กโฟลว์ได้จากแถบเครื่องมือไฟล์และเมนูคลิกขวา'
      },
      {
        type: 'improvement',
        text: 'Two-tier breadcrumb — page name on top, folder path below',
        th: 'เส้นทางสองชั้น: ชื่อหน้าอยู่บน เส้นทางโฟลเดอร์อยู่ล่าง'
      },
      { type: 'improvement', text: 'Folder icons recoloured to the brand blue', th: 'ไอคอนโฟลเดอร์เปลี่ยนเป็นสีน้ำเงินของแบรนด์' }
    ]
  },
  {
    version: '1.0.0',
    date: '2026-05-28',
    title: 'Clean Corporate redesign',
    titleTh: 'ออกแบบหน้าตาใหม่',
    changes: [
      {
        type: 'feature',
        text: 'New Dashboard landing page — stats, quick actions and recent items',
        th: 'หน้าหลักใหม่: สถิติ ทางลัด และเอกสารล่าสุด'
      },
      {
        type: 'feature',
        text: 'Workflow management — My Tasks, Start Workflow and Workflow Builder',
        th: 'จัดการเวิร์กโฟลว์: งานของฉัน เริ่มเวิร์กโฟลว์ และตัวสร้างเวิร์กโฟลว์'
      },
      {
        type: 'feature',
        text: 'Light / Dark theme with a fresh Alfresco-blue palette and Inter typography',
        th: 'ธีมสว่าง/มืด โทนสีน้ำเงิน และตัวอักษร Inter'
      },
      { type: 'improvement', text: 'Modernised sidebar with consistent icons and spacing', th: 'แถบเมนูใหม่ ไอคอนและระยะห่างสม่ำเสมอ' }
    ]
  }
];
